import type { JSONValue, ReasoningUIPart } from '@ai-sdk/ui-utils';
import { useStore } from '@nanostores/react';
import { AnimatePresence, motion, type Transition, type Variants } from 'framer-motion';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Markdown } from './Markdown';
import { AssistantThinking } from '~/components/chat/assistant-message/AssistantThinking';
import type { ProcessedPart } from '~/components/chat/assistant-message/types';
import { workbenchStore } from '~/lib/stores/workbench';
import type { Message } from '~/types/chat';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';
import { plural } from '~/utils/plural';
import { deserializeThinkingAnnotation, isThinkingAnnotation } from '~/utils/thinking';

interface ThinkingResult {
  content: string;
  done: boolean;
}

const variants = {
  closed: {
    height: 0,
    opacity: 0,
    visibility: 'hidden',
  },
  open: {
    height: 'auto',
    opacity: 1,
    visibility: 'visible',
  },
} satisfies Variants;

const transition = { duration: 0.25, ease: cubicEasingFn } satisfies Transition;

interface ThinkingProps {
  className?: string;
  content: string;
  annotations?: Message['annotations'];
  parts?: Message['parts'];
  lastVisiblePart?: ProcessedPart;
}

export const Thinking = memo(({ className, content, annotations, parts, lastVisiblePart }: ThinkingProps) => {
  const [open, setOpen] = useState(false);
  const [thinkingTime, setThinkingTime] = useState(0);

  const isStreaming = useStore(workbenchStore.isStreaming);

  const thinkingTimerRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const thinkingFromAnnotations = useThinkingAnnotations(annotations);
  const thinkingFromParts = useThinkingFromParts(content, parts);

  const thinking = thinkingFromParts || thinkingFromAnnotations;

  useEffect(() => {
    if (thinking?.content && !thinking.done && !thinkingTimerRef.current) {
      thinkingTimerRef.current = setInterval(() => {
        setThinkingTime((prev) => prev + 1);
      }, 1000);
    } else if (thinking?.done || !isStreaming) {
      clearInterval(thinkingTimerRef.current);
    }
  }, [thinking?.content, thinking?.done, isStreaming]);

  if (!lastVisiblePart && isStreaming && !thinking) {
    return <AssistantThinking includeIcon />;
  }

  if (!thinking) {
    return null;
  }

  const isThinking = !thinking.done && isStreaming;

  return (
    <div className={classNames('flex-col gap-2 overflow-hidden', className)}>
      <button
        className="flex items-center gap-1 h-10 bg-transparent text-sm text-bolt-elements-textSecondary hover:text-bolt-elements-textPrimary transition-theme cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <AnimatePresence>
          {isThinking ? (
            <AssistantThinking includeIcon />
          ) : (
            <span>{thinkingTime > 0 ? `Thought for ${formatThinkingTime(thinkingTime)}` : 'Thoughts'}</span>
          )}
        </AnimatePresence>
        <span className={classNames('size-4', open ? 'i-ph:caret-down' : 'i-ph:caret-right')} />
      </button>
      <motion.div
        initial="closed"
        animate={open ? 'open' : 'closed'}
        exit="closed"
        variants={variants}
        transition={transition}
      >
        <div className="relative pt-3.5 pl-3.5">
          <div className="absolute left-0 flex h-full w-[2px] shrink-0 flex-col items-center gap-1">
            <div className="bg-gray-300 dark:bg-bolt-ds-neutral h-full w-[1px] rounded-full" />
          </div>
          <div className="[--markdown-content-text-color:var(--bolt-elements-textSecondary)]">
            <Markdown html>{thinking.content}</Markdown>
          </div>
        </div>
      </motion.div>
    </div>
  );
});

/**
 * @deprecated Now that we upgraded the AI SDK we no longer use annotations for thinking output.
 * We keep this for a while for backwards compatibility.
 */
function useThinkingAnnotations(annotations?: JSONValue[]): ThinkingResult | undefined {
  return useMemo(() => {
    const thinkingAnnotations = annotations
      ?.filter((annotation) => isThinkingAnnotation(annotation))
      .map(deserializeThinkingAnnotation);

    if (!thinkingAnnotations?.length) {
      return undefined;
    }

    let combinedThinking = '';
    let done = false;

    for (const annotation of thinkingAnnotations) {
      combinedThinking += annotation.content || '';

      if (annotation.done) {
        done = true;
        break;
      }
    }

    return { content: combinedThinking, done };
  }, [annotations]);
}

function useThinkingFromParts(content: string, parts: Message['parts'] = []): ThinkingResult | undefined {
  return useMemo(() => {
    if (!parts?.length) {
      return undefined;
    }

    const thinkingParts = parts.filter((part) => part.type === 'reasoning' && part.reasoning) as ReasoningUIPart[];

    if (!thinkingParts.length) {
      return undefined;
    }

    // there should always only be a single reasoning part per message
    return { content: thinkingParts[0].reasoning, done: !!content };
  }, [parts]);
}

function formatThinkingTime(seconds: number) {
  if (seconds < 60) {
    return `${Math.floor(seconds)}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  return `${plural(minutes, 'min')} ${remainingSeconds}s`;
}
